import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  FileText, Receipt, ShieldCheck, Camera, Upload, Eye, Download,
  Loader2, Send, Link2, CheckCircle2, RefreshCw, AlertCircle, X, Trash2,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { formatDate } from '@/lib/dateUtils';
import FilePreviewModal from './FilePreviewModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Absolute URL for a possibly-relative download path (e.g. "/api/files/..").
const toAbs = (u) => (!u ? null : (u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`));

// In-app gallery for completion photos — thumbnails open the shared preview modal.
function PhotoGallery({ photos, onClose, onPreview }) {
  const urls = (photos || []).map((p) => {
    if (typeof p === 'string') return { url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`, name: `photo-${p}.jpg` };
    if (p?.download_url) return { url: toAbs(p.download_url), name: p.filename || 'photo.jpg' };
    if (p?.url) return { url: toAbs(p.url), name: p.filename || 'photo.jpg' };
    if (p?.file_id) return { url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${p.file_id}`, name: p.filename || `photo-${p.file_id}.jpg` };
    return null;
  }).filter(Boolean);
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 60, padding: 20, overflow: 'auto' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="docs-modal-photo-gallery"
    >
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-white font-semibold">Completion photos ({urls.length})</div>
          <button onClick={onClose} className="text-white p-1 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {urls.map((u, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onPreview?.({ url: u.url, filename: u.name })}
              className="block bg-slate-900 rounded-lg overflow-hidden border border-slate-700 hover:border-blue-400 transition-colors"
              style={{ padding: 0 }}
              data-testid={`docs-modal-photo-${i}`}
            >
              <img src={u.url} alt={`Completion photo ${i + 1}`} style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} />
            </button>
          ))}
          {urls.length === 0 && (
            <div className="text-slate-300 text-sm col-span-full text-center py-10">No photo URLs available.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Three layout options for the "View Docs" experience, presented inside
 * the same large centered modal style as LogPaymentDrawer
 * (`max-w-2xl max-h-[92vh]`). All three share `useDocsData()` so the
 * data shown is identical — only the visual organization differs.
 *
 *  - PhaseDocsModalScrollable   →  one long scrollable column
 *  - PhaseDocsModalTabs         →  Compliance · Receipts · History tabs
 *  - PhaseDocsModalAccordion    →  collapsible accordion sections
 *
 * `PhaseDocsModalTabs` is the production winner (user picked Option B
 * on Jun 24 2026). The Scrollable / Accordion variants are retained
 * for the layout-comparison preview page only.
 *
 * Action handlers (request new waiver, upload receipt, resend email,
 * copy contractor portal link) are real when `onRefresh` is provided
 * (production path from ConstructionPhasesV2). When the consumer passes
 * `onAction` instead (comparison preview), all actions toast a stub.
 */

// --------------------------- shared data hook ---------------------------

function useDocsData(phase, contractor) {
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [docStatus, setDocStatus] = useState(null);
  const [lienWaiverHistory, setLienWaiverHistory] = useState({ active: null, archived: [] });

  const load = useCallback(async () => {
    if (!phase?.id) return;
    setLoading(true);
    try {
      const reqs = [
        axios.get(`${API}/phases/${phase.id}/receipts`),
        axios.get(`${API}/phases/${phase.id}/lien-waiver-history`).catch(() => ({ data: null })),
      ];
      if (contractor?.id) {
        reqs.push(axios.get(`${API}/contractors/${contractor.id}/doc-status`).catch(() => ({ data: null })));
      }
      const results = await Promise.all(reqs);
      const recData = results[0]?.data || {};
      setReceipts(Array.isArray(recData.receipts) ? recData.receipts : (recData.items || []));
      const hist = results[1]?.data;
      if (hist) {
        setLienWaiverHistory({
          active: hist.active || null,
          archived: Array.isArray(hist.archived) ? hist.archived : [],
        });
      }
      if (results[2]?.data) setDocStatus(results[2].data);
    } finally {
      setLoading(false);
    }
  }, [phase?.id, contractor?.id]);

  useEffect(() => { load(); }, [load]);

  return { loading, receipts, docStatus, lienWaiverHistory, refresh: load };
}

// --------------------------- shared actions hook ---------------------------

function useDocsActions({ phase, contractor, refresh, onRefresh, onAction }) {
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [resending, setResending] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [removingDoc, setRemovingDoc] = useState(null);

  // Preview-mode short-circuit: when onAction is provided (comparison
  // page), stub all actions to a toast and skip the API calls.
  const isPreview = typeof onAction === 'function';

  const onAddReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isPreview) {
      onAction(`Add receipt (${file.name})`);
      e.target.value = '';
      return;
    }
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('add_to_actual_cost', 'false');
      await axios.post(`${API}/phases/${phase.id}/receipts`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Receipt uploaded');
      await refresh();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload receipt');
    } finally {
      setUploadingReceipt(false);
      e.target.value = '';
    }
  };

  const onRequestNewWaiver = async () => {
    if (isPreview) {
      onAction('Request new lien waiver');
      return;
    }
    const amount = phase?.lien_waiver_amount || phase?.invoice_amount || phase?.actual_cost || phase?.supplier_amount_paid;
    if (!amount) {
      toast.error('Need a phase invoice amount to request a new waiver.');
      return;
    }
    try {
      await axios.post(`${API}/phases/${phase.id}/request-new-lien-waiver`, { amount });
      toast.success('New lien waiver requested');
      await refresh();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to request new waiver');
    }
  };

  const onResendComplianceEmail = async () => {
    if (isPreview) {
      onAction('Resend compliance email');
      return;
    }
    const amount = phase?.lien_waiver_amount || phase?.invoice_amount || phase?.actual_cost || phase?.supplier_amount_paid;
    if (!amount || resending) return;
    setResending(true);
    try {
      await axios.post(`${API}/phases/${phase.id}/request-new-lien-waiver`, { amount });
      toast.success('Compliance email resent to contractor');
      await refresh();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to resend email');
    } finally {
      setResending(false);
    }
  };

  const onCopyPortalLink = async () => {
    if (isPreview) {
      onAction('Copy contractor portal link');
      return;
    }
    const token = phase?.compliance_portal_token || phase?.contractor_portal_token;
    if (!token) {
      toast.error('No portal token yet — send a compliance request first.');
      return;
    }
    const link = `${window.location.origin}/contractor-compliance?token=${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
      toast.success('Link copied');
    } catch {
      toast.error('Clipboard unavailable');
    }
  };

  const onRequestCoiRenewal = async () => {
    if (isPreview) {
      onAction('Request COI renewal');
      return;
    }
    try {
      const amount = phase?.lien_waiver_amount || phase?.invoice_amount || 0;
      await axios.post(`${API}/phases/${phase.id}/request-compliance-docs`, {
        invoice_amount: amount,
        include_coi: true,
      });
      toast.success('COI renewal request sent to contractor');
      await refresh();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to request COI renewal');
    }
  };

  const onRemoveComplianceDoc = async (key) => {
    if (isPreview) { onAction(`Remove ${key}`); return; }
    const confirmMsg = 'Remove this document? This deletes the current file so you can upload the correct one.';
    if (typeof window !== 'undefined' && !window.confirm(confirmMsg)) return;
    try {
      setRemovingDoc(key);
      if (key === 'w9' || key === 'coi') {
        if (!contractor?.id) { toast.error('No contractor is assigned to this item'); return; }
        await axios.delete(`${API}/contractors/${contractor.id}/${key}`);
      } else if (key === 'lien_waiver') {
        await axios.delete(`${API}/phases/${phase.id}/lien-waiver`);
      }
      toast.success('Document removed — you can upload the correct file now');
      await refresh();
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove document');
    } finally {
      setRemovingDoc(null);
    }
  };

  return {
    uploadingReceipt,
    resending,
    linkCopied,
    removingDoc,
    onAddReceipt,
    onRequestNewWaiver,
    onResendComplianceEmail,
    onCopyPortalLink,
    onRequestCoiRenewal,
    onRemoveComplianceDoc,
  };
}

// --------------------------- reusable section blocks ---------------------------

function ComplianceGrid({ docStatus, phase, onRequestCoiRenewal, onPreview, onViewPhotos, onRemove, removingDoc }) {
  const photos = phase?.completion_photos || [];
  const tiles = [
    {
      key: 'w9',
      label: 'W-9',
      icon: ShieldCheck,
      uploaded: !!docStatus?.w9?.on_file,
      date: docStatus?.w9?.received_date,
      href: docStatus?.w9?.download_url || null,
    },
    {
      key: 'coi',
      label: 'COI',
      icon: ShieldCheck,
      uploaded: !!docStatus?.coi?.on_file,
      sub: docStatus?.coi?.expiration ? `Expires ${formatDate(docStatus.coi.expiration)}` : null,
      expired: !!docStatus?.coi?.expired,
      href: docStatus?.coi?.download_url || null,
    },
    {
      key: 'lien_waiver',
      label: 'Lien Waiver',
      icon: FileText,
      uploaded: !!(phase?.lien_waiver_signed || phase?.lien_waiver_file_id),
      date: phase?.lien_waiver_signed_at,
      href: phase?.id ? `${API}/phases/${phase.id}/lien-waiver-pdf` : null,
    },
    {
      key: 'completion_photos',
      label: 'Completion photos',
      icon: Camera,
      uploaded: !!(photos && photos.length),
      sub: photos?.length
        ? `${photos.length} photo${photos.length > 1 ? 's' : ''}`
        : null,
      isPhotos: true,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="docs-modal-compliance-grid">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={`rounded-lg border p-3 ${
            t.uploaded
              ? t.expired
                ? 'border-rose-300 bg-rose-50'
                : 'border-emerald-300 bg-emerald-50'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div className="flex items-start gap-2">
            <t.icon
              className={`w-4 h-4 mt-0.5 ${
                t.uploaded ? (t.expired ? 'text-rose-600' : 'text-emerald-600') : 'text-slate-400'
              }`}
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                {t.label}
                {t.uploaded && t.isPhotos && (
                  <button
                    type="button"
                    onClick={() => onViewPhotos?.(photos)}
                    className="text-slate-400 hover:text-blue-600"
                    title="View photos"
                    data-testid="compliance-tile-view-photos"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                )}
                {t.uploaded && !t.isPhotos && t.href && (
                  <button
                    type="button"
                    onClick={() => onPreview?.({ url: toAbs(t.href), filename: `${t.label}.pdf` })}
                    className="text-slate-400 hover:text-blue-600"
                    title="Preview"
                    data-testid={`compliance-tile-preview-${t.key}`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="text-[11px] text-slate-500">
                {!t.uploaded
                  ? 'Not on file'
                  : t.sub || (t.date ? formatDate(t.date) : 'Uploaded')}
              </div>
              {t.isPhotos && t.uploaded && (
                <button
                  type="button"
                  onClick={() => onViewPhotos?.(photos)}
                  className="text-[11px] text-blue-600 hover:text-blue-800 font-medium mt-1"
                  data-testid="compliance-tile-view-photos-link"
                >
                  View photos →
                </button>
              )}
              {t.key === 'coi' && t.expired && onRequestCoiRenewal && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onRequestCoiRenewal}
                  className="h-6 text-[11px] mt-2 border-rose-300 text-rose-700 hover:bg-rose-100"
                  data-testid="compliance-coi-request-renewal-btn"
                >
                  <RefreshCw className="w-3 h-3 mr-1" /> Request renewal
                </Button>
              )}
              {t.uploaded && !t.isPhotos && onRemove && (
                <button
                  type="button"
                  disabled={removingDoc === t.key}
                  onClick={() => onRemove(t.key)}
                  className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-rose-600 hover:text-rose-800 disabled:opacity-50"
                  data-testid={`compliance-remove-${t.key}`}
                  title="Remove this document so you can upload the correct one"
                >
                  {removingDoc === t.key
                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Removing…</>
                    : <><Trash2 className="w-3 h-3" /> Remove / replace</>}
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReceiptsList({ receipts, onAddReceipt, uploading, onPreview }) {
  const nonRefund = receipts.filter((r) => !r.net_refund && r?.category !== 'refund');
  return (
    <div data-testid="docs-modal-receipts">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Receipts &amp; invoices
        </div>
        <label className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1 cursor-pointer font-medium">
          {uploading
            ? <><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</>
            : <><Upload className="w-3 h-3" /> Add receipt</>}
          <input
            type="file"
            className="hidden"
            onChange={onAddReceipt}
            disabled={uploading}
            data-testid="docs-modal-add-receipt"
          />
        </label>
      </div>
      {nonRefund.length === 0 ? (
        <div className="text-xs text-slate-500 italic py-4 text-center bg-slate-50 rounded">
          No receipts uploaded for this phase.
        </div>
      ) : (
        <div className="space-y-2">
          {nonRefund.map((r) => {
            const dl = r.download_url
              ? (r.download_url.startsWith('http') ? r.download_url : `${process.env.REACT_APP_BACKEND_URL}${r.download_url}`)
              : null;
            const fname = r.filename || r.vendor || 'Receipt';
            return (
              <div
                key={r.id || r.file_id}
                className="flex items-center gap-3 bg-white rounded-lg border border-slate-200 p-3 text-sm"
              >
                <Receipt className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-slate-900 truncate font-medium">{fname}</div>
                  <div className="text-xs text-slate-500">
                    {r.amount != null && <>${Number(r.amount).toLocaleString()}</>}
                    {r.amount != null && (r.upload_date || r.uploaded_at) && ' · '}
                    {(r.upload_date || r.uploaded_at) && formatDate(r.upload_date || r.uploaded_at)}
                  </div>
                </div>
                {dl && (
                  <>
                    <button type="button" onClick={() => (onPreview ? onPreview({ url: dl, filename: fname }) : window.open(dl, '_blank'))} className="text-slate-400 hover:text-blue-600 p-1" title="Preview" data-testid="receipt-preview-btn">
                      <Eye className="w-4 h-4" />
                    </button>
                    <a href={dl} download={fname} className="text-slate-400 hover:text-blue-600 p-1" title="Download">
                      <Download className="w-4 h-4" />
                    </a>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LienWaiverHistorySection({ phase, lienWaiverHistory, onRequestNew, onPreview }) {
  const active = lienWaiverHistory.active;
  const archived = lienWaiverHistory.archived || [];
  return (
    <div data-testid="docs-modal-lien-waiver-history">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Lien Waiver — per draw
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onRequestNew}
          className="h-7 text-xs"
          data-testid="docs-modal-request-new-waiver-btn"
        >
          <RefreshCw className="w-3 h-3 mr-1" /> Request new
        </Button>
      </div>
      {active ? (
        <div className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 mb-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
            <CheckCircle2 className="w-4 h-4" /> Active waiver
            {active.amount ? <span className="ml-auto">${Number(active.amount).toLocaleString()}</span> : null}
          </div>
          <div className="text-[11px] text-emerald-800 mt-1 flex items-center gap-2">
            <span>
              {active.signed ? 'Signed' : 'Pending'} ·
              {active.signed_at ? ` ${formatDate(active.signed_at)}` : ' awaiting signature'}
            </span>
            {phase?.id && (
              <button
                type="button"
                onClick={() => (onPreview ? onPreview({ url: `${API}/phases/${phase.id}/lien-waiver-pdf`, filename: 'Lien Waiver.pdf' }) : window.open(`${API}/phases/${phase.id}/lien-waiver-pdf`, '_blank'))}
                className="ml-auto text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                data-testid="docs-modal-view-active-waiver"
              >
                <Eye className="w-3.5 h-3.5" /> View PDF
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="text-xs text-slate-500 italic py-2">No active waiver on file.</div>
      )}
      {archived.length > 0 && (
        <>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mt-3 mb-1.5">
            Previous waivers ({archived.length})
          </div>
          <div className="space-y-1.5">
            {archived.slice().reverse().map((e) => (
              <div key={e.id} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-2.5 text-xs">
                <FileText className="w-4 h-4 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">
                    {e.amount ? `$${Number(e.amount).toLocaleString()}` : 'Archived waiver'}
                    {e.claimant_name ? ` · ${e.claimant_name}` : ''}
                  </div>
                  <div className="text-slate-500">{formatDate(e.signed_at || e.archived_at)}</div>
                </div>
                <button
                  type="button"
                  onClick={() => (onPreview ? onPreview({ url: `${API}/phases/${phase.id}/lien-waiver-history/${e.id}/pdf`, filename: 'Lien Waiver.pdf' }) : window.open(`${API}/phases/${phase.id}/lien-waiver-history/${e.id}/pdf`, '_blank'))}
                  className="text-slate-400 hover:text-blue-600 p-1"
                  data-testid={`docs-modal-view-archived-${e.id}`}
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function PendingComplianceBanner({ phase, contractor, onResend, onCopyLink, linkCopied, resending }) {
  const reqSentAt = phase?.compliance_request_sent_at || phase?.lien_waiver_request_sent_at;
  const signedAt = phase?.lien_waiver_signed_at;
  const isPending = phase?.compliance_request_sent
    && reqSentAt
    && (!signedAt || new Date(reqSentAt) > new Date(signedAt));
  if (!isPending) return null;
  const requestedAmount = phase?.lien_waiver_amount || phase?.invoice_amount;
  const stillPending = [];
  if (!phase?.lien_waiver_signed) stillPending.push('Lien waiver');
  if (phase?.phase_type !== 'soft_cost' && !phase?.completion_photos_uploaded) stillPending.push('Completion photos');
  if (phase?.requires_inspection && !phase?.inspection_passed) stillPending.push('Inspection');
  return (
    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3" data-testid="docs-modal-pending-banner">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-900">
            Compliance request pending
            {requestedAmount ? <> · ${Number(requestedAmount).toLocaleString()}</> : null}
          </div>
          <div className="text-[11px] text-amber-800 mt-0.5">
            Sent {reqSentAt ? formatDate(reqSentAt) : 'recently'} · waiting on{' '}
            {contractor?.company || contractor?.name || 'contractor'}
          </div>
          {stillPending.length > 0 && (
            <div className="text-[11px] text-amber-900 mt-1">
              <span className="font-semibold">Still needed:</span> {stillPending.join(' · ')}
            </div>
          )}
          <div className="flex flex-wrap gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={onCopyLink} className="h-7 text-xs">
              {linkCopied ? <><CheckCircle2 className="w-3 h-3 mr-1" /> Copied</> : <><Link2 className="w-3 h-3 mr-1" /> Copy link</>}
            </Button>
            <Button size="sm" onClick={onResend} disabled={resending} className="h-7 text-xs bg-amber-600 hover:bg-amber-700">
              {resending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Resend email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------- shared header ---------------------------

function ModalHeader({ phase, variantLabel }) {
  return (
    <div className="px-6 pt-6 pb-3 border-b">
      <div className="flex items-baseline gap-2">
        <DialogTitle asChild>
          <h2 className="text-lg font-semibold text-slate-900" data-testid="docs-modal-title">
            Phase Documents
          </h2>
        </DialogTitle>
        {variantLabel && (
          <span className="text-xs uppercase tracking-wide font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded">
            {variantLabel}
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-1">
        {phase?.name} {phase?.contractor_name ? `· ${phase.contractor_name}` : ''}
      </p>
    </div>
  );
}

// --------------------------- Variant 1: Scrollable (preview only) ---------------------------

export function PhaseDocsModalScrollable({ isOpen, onClose, phase, contractor, onAction, onRefresh }) {
  const { loading, receipts, docStatus, lienWaiverHistory, refresh } = useDocsData(phase, contractor);
  const actions = useDocsActions({ phase, contractor, refresh, onRefresh, onAction });
  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="docs-modal-scrollable">
        <ModalHeader phase={phase} variantLabel={onAction ? 'Scrollable' : null} />
        <div className="px-6 py-5 space-y-6">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <PendingComplianceBanner
                phase={phase}
                contractor={contractor}
                onResend={actions.onResendComplianceEmail}
                onCopyLink={actions.onCopyPortalLink}
                linkCopied={actions.linkCopied}
                resending={actions.resending}
              />
              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Compliance</div>
                <ComplianceGrid docStatus={docStatus} phase={phase} onRequestCoiRenewal={actions.onRequestCoiRenewal} onRemove={actions.onRemoveComplianceDoc} removingDoc={actions.removingDoc} />
              </section>
              <section>
                <ReceiptsList receipts={receipts} onAddReceipt={actions.onAddReceipt} uploading={actions.uploadingReceipt} />
              </section>
              <section>
                <LienWaiverHistorySection
                  phase={phase}
                  lienWaiverHistory={lienWaiverHistory}
                  onRequestNew={actions.onRequestNewWaiver}
                />
              </section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------- Variant 2: Tabs (PRODUCTION) ---------------------------

export function PhaseDocsModalTabs({ isOpen, onClose, phase, contractor, onAction, onRefresh }) {
  const { loading, receipts, docStatus, lienWaiverHistory, refresh } = useDocsData(phase, contractor);
  const actions = useDocsActions({ phase, contractor, refresh, onRefresh, onAction });
  const receiptCount = receipts.filter((r) => !r.net_refund && r?.category !== 'refund').length;
  const [preview, setPreview] = useState(null);      // { url, filename }
  const [galleryPhotos, setGalleryPhotos] = useState(null); // array | null
  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="docs-modal-tabs">
        <ModalHeader phase={phase} variantLabel={onAction ? 'Tabs' : null} />
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : (
          <Tabs defaultValue="compliance" className="px-6 py-4">
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="compliance" data-testid="docs-tab-compliance">Compliance</TabsTrigger>
              <TabsTrigger value="receipts" data-testid="docs-tab-receipts">
                Receipts
                {receiptCount > 0 && (
                  <span className="ml-1 text-[10px] bg-slate-200 px-1.5 rounded">{receiptCount}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="history" data-testid="docs-tab-history">
                Waiver History
                {lienWaiverHistory.archived.length > 0 && (
                  <span className="ml-1 text-[10px] bg-slate-200 px-1.5 rounded">
                    {lienWaiverHistory.archived.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="compliance" className="space-y-4 min-h-[280px]">
              <PendingComplianceBanner
                phase={phase}
                contractor={contractor}
                onResend={actions.onResendComplianceEmail}
                onCopyLink={actions.onCopyPortalLink}
                linkCopied={actions.linkCopied}
                resending={actions.resending}
              />
              <ComplianceGrid
                docStatus={docStatus}
                phase={phase}
                onRequestCoiRenewal={actions.onRequestCoiRenewal}
                onPreview={setPreview}
                onViewPhotos={setGalleryPhotos}
                onRemove={actions.onRemoveComplianceDoc}
                removingDoc={actions.removingDoc}
              />
            </TabsContent>
            <TabsContent value="receipts" className="min-h-[280px]">
              <ReceiptsList receipts={receipts} onAddReceipt={actions.onAddReceipt} uploading={actions.uploadingReceipt} onPreview={setPreview} />
            </TabsContent>
            <TabsContent value="history" className="min-h-[280px]">
              <LienWaiverHistorySection
                phase={phase}
                lienWaiverHistory={lienWaiverHistory}
                onRequestNew={actions.onRequestNewWaiver}
                onPreview={setPreview}
              />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
      {galleryPhotos && (
        <PhotoGallery
          photos={galleryPhotos}
          onClose={() => setGalleryPhotos(null)}
          onPreview={setPreview}
        />
      )}
      {preview && (
        <FilePreviewModal url={preview.url} filename={preview.filename} onClose={() => setPreview(null)} />
      )}
    </Dialog>
  );
}

// --------------------------- Variant 3: Accordion (preview only) ---------------------------

export function PhaseDocsModalAccordion({ isOpen, onClose, phase, contractor, onAction, onRefresh }) {
  const { loading, receipts, docStatus, lienWaiverHistory, refresh } = useDocsData(phase, contractor);
  const actions = useDocsActions({ phase, contractor, refresh, onRefresh, onAction });
  const receiptCount = receipts.filter((r) => !r.net_refund && r?.category !== 'refund').length;
  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="docs-modal-accordion">
        <ModalHeader phase={phase} variantLabel={onAction ? 'Accordion' : null} />
        <div className="px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : (
            <>
              <PendingComplianceBanner
                phase={phase}
                contractor={contractor}
                onResend={actions.onResendComplianceEmail}
                onCopyLink={actions.onCopyPortalLink}
                linkCopied={actions.linkCopied}
                resending={actions.resending}
              />
              <Accordion type="multiple" defaultValue={["compliance"]} className="mt-2">
                <AccordionItem value="compliance">
                  <AccordionTrigger className="text-sm font-semibold" data-testid="docs-acc-compliance">
                    Compliance
                  </AccordionTrigger>
                  <AccordionContent>
                    <ComplianceGrid docStatus={docStatus} phase={phase} onRequestCoiRenewal={actions.onRequestCoiRenewal} onRemove={actions.onRemoveComplianceDoc} removingDoc={actions.removingDoc} />
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="receipts">
                  <AccordionTrigger className="text-sm font-semibold" data-testid="docs-acc-receipts">
                    Receipts &amp; invoices
                    {receiptCount > 0 && (
                      <span className="ml-2 text-[10px] bg-slate-200 px-2 py-0.5 rounded">{receiptCount}</span>
                    )}
                  </AccordionTrigger>
                  <AccordionContent>
                    <ReceiptsList receipts={receipts} onAddReceipt={actions.onAddReceipt} uploading={actions.uploadingReceipt} />
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="history">
                  <AccordionTrigger className="text-sm font-semibold" data-testid="docs-acc-history">
                    Lien Waiver — per draw
                    {lienWaiverHistory.archived.length > 0 && (
                      <span className="ml-2 text-[10px] bg-slate-200 px-2 py-0.5 rounded">
                        {lienWaiverHistory.archived.length}
                      </span>
                    )}
                  </AccordionTrigger>
                  <AccordionContent>
                    <LienWaiverHistorySection
                      phase={phase}
                      lienWaiverHistory={lienWaiverHistory}
                      onRequestNew={actions.onRequestNewWaiver}
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
